import React, { useState, useCallback } from 'react';
import Button from '../../shared/design-system/components/atoms/button';
import Avatar from '../../shared/design-system/components/atoms/initials-avatar';
import Badge from '../../shared/design-system/components/atoms/badge';
import {
  COLOR_FAMILIES,
  SEMANTIC_ROLES,
  TYPE_SCALE,
  FONT_WEIGHTS,
  FONT_SIZES,
  SPACING_VALUES,
  GAP_VALUES,
  BORDER_RADII,
  SHADOWS,
  SIDEBAR_SECTIONS,
} from './design-system-page.data';
import DesignSystemComponents from './design-system-components';

const BUTTON_VARIANTS = [
  Button.Variant.Default,
  Button.Variant.Primary,
  Button.Variant.Outlined,
  Button.Variant.Danger,
];

const BUTTON_SIZES = [Button.Size.Small, Button.Size.Medium, Button.Size.Large];

const onEnterKey = (handler: () => void) => (e: React.KeyboardEvent) => {
  if (e.key === 'Enter') handler();
};

const DesignSystemPage: React.FC = () => {
  const [toastText, setToastText] = useState('');
  const [toastVisible, setToastVisible] = useState(false);

  const copyToClipboard = useCallback((text: string) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setToastText(`Copied: ${text}`);
        setToastVisible(true);
        setTimeout(() => setToastVisible(false), 1500);
      },
      () => {
        setToastText('Copy failed');
        setToastVisible(true);
        setTimeout(() => setToastVisible(false), 1500);
      },
    );
  }, []);

  return (
    <div className="bs-design-system-page">
      {/* Header */}
      <div className="bs-design-system-page__header">
        <h1 className="semibold-5 gray-txt-9 mb-1">Design System Reference</h1>
        <p className="regular-2 gray-txt-15">
          Visual reference for all design tokens. Click any swatch or variable to copy.
        </p>
      </div>

      <div className="bs-design-system-page__layout">
        {/* Sidebar Nav */}
        <nav className="bs-design-system-page__sidebar">
          <ul className="bs-design-system-page__sidebar-nav">
            {SIDEBAR_SECTIONS.map((section) => (
              <li key={section.id}>
                <a href={`#${section.id}`} className="regular-2">
                  {section.label}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {/* Main Content */}
        <div className="bs-design-system-page__content">
          {/* 1. Colors */}
          <section id="colors" className="bs-design-system-page__section">
            <h2 className="semibold-4 gray-txt-9 bs-design-system-page__section-title">
              Color Palette
            </h2>

            {/* Semantic Roles */}
            <h3 className="semibold-3 gray-txt-9 bs-design-system-page__section-subtitle">
              Semantic Roles
            </h3>
            <div className="bs-design-system-page__semantic-grid">
              {SEMANTIC_ROLES.map((role) => (
                <div
                  key={role.role}
                  className="bs-design-system-page__semantic-card"
                  onClick={() => copyToClipboard(role.variable)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={onEnterKey(() => copyToClipboard(role.variable))}
                >
                  <div
                    className="bs-design-system-page__semantic-card-color"
                    style={{ backgroundColor: role.hex }}
                  />
                  <div className="bs-design-system-page__semantic-card-info">
                    <p className="semibold-2 gray-txt-9 mb-0">{role.role}</p>
                    <p className="regular-1 gray-txt-15 mb-0">{role.variable}</p>
                    <p className="regular-1 gray-txt-11 mb-0">{role.hex}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Color Families */}
            {COLOR_FAMILIES.map((family) => (
              <div key={family.name}>
                <h3 className="semibold-3 gray-txt-9 bs-design-system-page__section-subtitle">
                  {family.name}
                </h3>
                <div className="bs-design-system-page__color-grid">
                  {family.swatches.map((swatch) => (
                    <div
                      key={swatch.variable}
                      className="bs-design-system-page__color-swatch"
                      onClick={() => copyToClipboard(swatch.variable)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={onEnterKey(() => copyToClipboard(swatch.variable))}
                    >
                      <div
                        className="bs-design-system-page__color-swatch-color"
                        style={{ backgroundColor: swatch.hex }}
                      />
                      <div className="bs-design-system-page__color-swatch-info">
                        <p className="semibold-1 gray-txt-9 mb-0">{swatch.variable}</p>
                        <p className="regular-1 gray-txt-11 mb-0">{swatch.hex}</p>
                        <p className="regular-1 gray-txt-15 mb-0">
                          .{family.prefix}-{swatch.shade}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </section>

          {/* 2. Typography */}
          <section id="typography" className="bs-design-system-page__section">
            <h2 className="semibold-4 gray-txt-9 bs-design-system-page__section-title">
              Typography
            </h2>

            {/* Font Family */}
            <h3 className="semibold-3 gray-txt-9 bs-design-system-page__section-subtitle">
              Font Family
            </h3>
            <div className="bs-design-system-page__type-sample">
              <span className="regular-1 gray-txt-11 bs-design-system-page__type-sample-label">
                Inter (Primary)
              </span>
              <span className="regular-3 gray-txt-9">
                The quick brown fox jumps over the lazy dog
              </span>
            </div>

            {/* Regular Type Scale */}
            <h3 className="semibold-3 gray-txt-9 bs-design-system-page__section-subtitle">
              Regular Type Scale
            </h3>
            {TYPE_SCALE.map((item) => (
              <div
                key={`regular-${item.level}`}
                className="bs-design-system-page__type-sample"
                onClick={() => copyToClipboard(`.regular-${item.level}`)}
                role="button"
                tabIndex={0}
                onKeyDown={onEnterKey(() => copyToClipboard(`.regular-${item.level}`))}
              >
                <span className="regular-1 gray-txt-11 bs-design-system-page__type-sample-label">
                  .regular-{item.level}
                  <br />
                  {item.size} / {item.lineHeight}
                </span>
                <span className={`regular-${item.level} gray-txt-9`}>
                  The quick brown fox jumps
                </span>
              </div>
            ))}

            {/* Semibold Type Scale */}
            <h3 className="semibold-3 gray-txt-9 bs-design-system-page__section-subtitle">
              Semibold Type Scale
            </h3>
            {TYPE_SCALE.map((item) => (
              <div
                key={`semibold-${item.level}`}
                className="bs-design-system-page__type-sample"
                onClick={() => copyToClipboard(`.semibold-${item.level}`)}
                role="button"
                tabIndex={0}
                onKeyDown={onEnterKey(() => copyToClipboard(`.semibold-${item.level}`))}
              >
                <span className="regular-1 gray-txt-11 bs-design-system-page__type-sample-label">
                  .semibold-{item.level}
                  <br />
                  {item.size} / {item.lineHeight}
                </span>
                <span className={`semibold-${item.level} gray-txt-9`}>
                  The quick brown fox jumps
                </span>
              </div>
            ))}

            {/* Font Weights */}
            <h3 className="semibold-3 gray-txt-9 bs-design-system-page__section-subtitle">
              Font Weights
            </h3>
            {FONT_WEIGHTS.map((weight) => (
              <div
                key={weight.value}
                className="bs-design-system-page__weight-sample"
                onClick={() => copyToClipboard(weight.variable)}
                role="button"
                tabIndex={0}
                onKeyDown={onEnterKey(() => copyToClipboard(weight.variable))}
              >
                <span className="regular-1 gray-txt-11 bs-design-system-page__weight-sample-label">
                  .{weight.className} ({weight.value})
                </span>
                <span className={`regular-3 gray-txt-9 ${weight.className}`}>
                  The quick brown fox jumps over the lazy dog
                </span>
              </div>
            ))}

            {/* Font Size Utilities */}
            <h3 className="semibold-3 gray-txt-9 bs-design-system-page__section-subtitle">
              Font Size Utilities
            </h3>
            {FONT_SIZES.map((fs) => (
              <div
                key={fs.label}
                className="bs-design-system-page__type-sample"
                onClick={() => copyToClipboard(fs.label)}
                role="button"
                tabIndex={0}
                onKeyDown={onEnterKey(() => copyToClipboard(fs.label))}
              >
                <span className="regular-1 gray-txt-11 bs-design-system-page__type-sample-label">
                  {fs.label} ({fs.size})
                </span>
                <span className={`${fs.cssClass} gray-txt-9`}>Sample text at {fs.size}</span>
              </div>
            ))}
          </section>

          {/* 3. Spacing */}
          <section id="spacing" className="bs-design-system-page__section">
            <h2 className="semibold-4 gray-txt-9 bs-design-system-page__section-title">
              Spacing
            </h2>

            {/* SCSS Variables */}
            <h3 className="semibold-3 gray-txt-9 bs-design-system-page__section-subtitle">
              SCSS Variables
            </h3>
            {SPACING_VALUES.map((spacing) => (
              <div
                key={spacing.variable}
                className="bs-design-system-page__spacing-row"
                onClick={() => copyToClipboard(spacing.variable)}
                role="button"
                tabIndex={0}
                onKeyDown={onEnterKey(() => copyToClipboard(spacing.variable))}
              >
                <span className="regular-1 gray-txt-11 bs-design-system-page__spacing-row-label">
                  {spacing.variable}
                </span>
                <div
                  className="bs-design-system-page__spacing-row-bar"
                  style={{ width: `${spacing.width * 4}px` }}
                />
                <span className="regular-1 gray-txt-15 bs-design-system-page__spacing-row-value">
                  {spacing.value}
                </span>
              </div>
            ))}

            {/* Gap Utilities */}
            <h3 className="semibold-3 gray-txt-9 bs-design-system-page__section-subtitle">
              Gap Utilities
            </h3>
            {GAP_VALUES.map((gap) => (
              <div
                key={gap.label}
                className="bs-design-system-page__spacing-row"
                onClick={() => copyToClipboard(gap.label)}
                role="button"
                tabIndex={0}
                onKeyDown={onEnterKey(() => copyToClipboard(gap.label))}
              >
                <span className="regular-1 gray-txt-11 bs-design-system-page__spacing-row-label">
                  {gap.label}
                </span>
                <div
                  className="bs-design-system-page__spacing-row-bar"
                  style={{ width: `${gap.width * 4}px` }}
                />
                <span className="regular-1 gray-txt-15 bs-design-system-page__spacing-row-value">
                  {gap.value}
                </span>
              </div>
            ))}
          </section>

          {/* 4. Border Radius */}
          <section id="borders" className="bs-design-system-page__section">
            <h2 className="semibold-4 gray-txt-9 bs-design-system-page__section-title">
              Border Radius
            </h2>
            <div className="bs-design-system-page__radius-grid">
              {BORDER_RADII.map((radius) => (
                <div
                  key={radius.variable}
                  className="bs-design-system-page__radius-demo"
                  onClick={() => copyToClipboard(radius.variable)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={onEnterKey(() => copyToClipboard(radius.variable))}
                >
                  <div
                    className="bs-design-system-page__radius-demo-box"
                    style={{ borderRadius: radius.value }}
                  />
                  <span className="semibold-1 gray-txt-9">{radius.name}</span>
                  <span className="regular-1 gray-txt-11">{radius.variable}</span>
                </div>
              ))}
            </div>
          </section>

          {/* 5. Shadows */}
          <section id="shadows" className="bs-design-system-page__section">
            <h2 className="semibold-4 gray-txt-9 bs-design-system-page__section-title">
              Shadows
            </h2>
            <div className="bs-design-system-page__shadow-grid">
              {SHADOWS.map((shadow) => (
                <div
                  key={shadow.variable}
                  className="bs-design-system-page__shadow-demo"
                  onClick={() => copyToClipboard(shadow.variable)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={onEnterKey(() => copyToClipboard(shadow.variable))}
                >
                  <div
                    className="bs-design-system-page__shadow-demo-box"
                    style={{ boxShadow: shadow.value }}
                  />
                  <span className="semibold-1 gray-txt-9">{shadow.name}</span>
                  <span className="regular-1 gray-txt-11">{shadow.variable}</span>
                </div>
              ))}
            </div>
          </section>

          {/* 6. Buttons */}
          <section id="buttons" className="bs-design-system-page__section">
            <h2 className="semibold-4 gray-txt-9 bs-design-system-page__section-title">
              Buttons
            </h2>

            {/* Solid Theme */}
            <h3 className="semibold-3 gray-txt-9 bs-design-system-page__section-subtitle">
              Solid Theme
            </h3>
            {BUTTON_VARIANTS.map((variant) => (
              <div key={`solid-${variant}`} className="bs-design-system-page__button-group">
                <p className="regular-1 gray-txt-11 mb-2">
                  Variant: {variant} / Theme: solid
                </p>
                <div className="bs-design-system-page__button-grid">
                  {BUTTON_SIZES.map((size) => (
                    <Button
                      key={`solid-${variant}-${size}`}
                      variant={variant}
                      theme={Button.Theme.Solid}
                      size={size}
                    >
                      {variant} {size}
                    </Button>
                  ))}
                  <Button variant={variant} theme={Button.Theme.Solid} disabled>
                    Disabled
                  </Button>
                  <Button variant={variant} theme={Button.Theme.Solid} isLoading>
                    Loading
                  </Button>
                </div>
              </div>
            ))}

            {/* Ghost Theme */}
            <h3 className="semibold-3 gray-txt-9 bs-design-system-page__section-subtitle">
              Ghost Theme
            </h3>
            {[Button.Variant.Primary, Button.Variant.Outlined, Button.Variant.Danger].map(
              (variant) => (
                <div key={`ghost-${variant}`} className="bs-design-system-page__button-group">
                  <p className="regular-1 gray-txt-11 mb-2">
                    Variant: {variant} / Theme: ghost
                  </p>
                  <div className="bs-design-system-page__button-grid">
                    {BUTTON_SIZES.map((size) => (
                      <Button
                        key={`ghost-${variant}-${size}`}
                        variant={variant}
                        theme={Button.Theme.Ghost}
                        size={size}
                      >
                        {variant} {size}
                      </Button>
                    ))}
                    <Button variant={variant} theme={Button.Theme.Ghost} disabled>
                      Disabled
                    </Button>
                  </div>
                </div>
              ),
            )}
          </section>

          {/* 7. Avatars */}
          <section id="avatars" className="bs-design-system-page__section">
            <h2 className="semibold-4 gray-txt-9 bs-design-system-page__section-title">
              Avatars
            </h2>
            <div className="bs-design-system-page__avatar-grid">
              <div className="bs-design-system-page__avatar-demo">
                <Avatar firstName="John" lastName="Doe" className="bs-avatar-sm" />
                <span className="regular-1 gray-txt-11">Small (.bs-avatar-sm, 24px)</span>
              </div>
              <div className="bs-design-system-page__avatar-demo">
                <Avatar firstName="Jane" lastName="Smith" />
                <span className="regular-1 gray-txt-11">Default (.bs-avatar-md, 27px)</span>
              </div>
              <div className="bs-design-system-page__avatar-demo">
                <Avatar firstName="Alex" lastName="Brown" className="bs-avatar-lg" />
                <span className="regular-1 gray-txt-11">Large (.bs-avatar-lg, 40px)</span>
              </div>
            </div>
          </section>

          {/* 8. Badges */}
          <section id="badges" className="bs-design-system-page__section">
            <h2 className="semibold-4 gray-txt-9 bs-design-system-page__section-title">
              Badges
            </h2>
            <div className="bs-design-system-page__badge-grid">
              <div className="bs-design-system-page__badge-demo">
                <Badge className="bs-badge-status-success" />
                <span className="regular-1 gray-txt-11">Success</span>
              </div>
              <div className="bs-design-system-page__badge-demo">
                <Badge className="bs-badge-status-error" />
                <span className="regular-1 gray-txt-11">Error</span>
              </div>
              <div className="bs-design-system-page__badge-demo">
                <Badge className="bs-badge-status-warning" />
                <span className="regular-1 gray-txt-11">Warning</span>
              </div>
              <div className="bs-design-system-page__badge-demo">
                <Badge className="bs-badge-status-processing" />
                <span className="regular-1 gray-txt-11">Processing</span>
              </div>
            </div>

            {/* Status Badge Colors */}
            <h3 className="semibold-3 gray-txt-9 bs-design-system-page__section-subtitle">
              Status Badge Colors
            </h3>
            <div className="d-flex flex-wrap gap-12">
              <div className="bs-design-system-page__status-badge bs-design-system-page__status-badge--success">
                <span className="regular-2">Success Badge</span>
              </div>
              <div className="bs-design-system-page__status-badge bs-design-system-page__status-badge--error">
                <span className="regular-2">Error Badge</span>
              </div>
              <div className="bs-design-system-page__status-badge bs-design-system-page__status-badge--warning">
                <span className="regular-2">Warning Badge</span>
              </div>
              <div className="bs-design-system-page__status-badge bs-design-system-page__status-badge--info">
                <span className="regular-2">Info Badge</span>
              </div>
              <div className="bs-design-system-page__status-badge bs-design-system-page__status-badge--neutral">
                <span className="regular-2">Neutral Badge</span>
              </div>
            </div>
          </section>

          {/* Component Sections (9-17) */}
          <DesignSystemComponents />
        </div>
      </div>

      {/* Copy Toast */}
      <div
        className={`bs-design-system-page__toast regular-2${
          toastVisible ? ' bs-design-system-page__toast--visible' : ''
        }`}
      >
        {toastText}
      </div>
    </div>
  );
};

export default DesignSystemPage;
